import { deviceStorage } from "./deviceStorage.js";
import {
  PMS_LAB_CLIENTS,
  PMS_LAB_FIRM,
  PMS_LAB_MATTERS,
  PMS_LAB_STAFF,
} from "./pmsLabData.js";

export const PMS_LAB_STORAGE_KEY = "cls_pms_lab_workspace_v1";
export const PMS_LAB_SCHEMA_VERSION = 1;

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function createPmsLabSeed() {
  return {
    schemaVersion: PMS_LAB_SCHEMA_VERSION,
    tenantId: PMS_LAB_FIRM.id,
    firm: clone(PMS_LAB_FIRM),
    staff: clone(PMS_LAB_STAFF),
    clients: clone(PMS_LAB_CLIENTS),
    matters: clone(PMS_LAB_MATTERS),
    updatedAt: new Date().toISOString(),
  };
}

export function validatePmsLabWorkspace(value) {
  if (!value || typeof value !== "object") return false;
  if (value.schemaVersion !== PMS_LAB_SCHEMA_VERSION) return false;
  if (value.tenantId !== PMS_LAB_FIRM.id) return false;
  if (!Array.isArray(value.clients) || !Array.isArray(value.matters) || !Array.isArray(value.staff)) return false;
  const clientIds = new Set(value.clients.map((client) => client.id));
  const staffIds = new Set(value.staff.map((staff) => staff.id));
  return value.matters.every((matter) => {
    if (!matter.id || !matter.clientId || !clientIds.has(matter.clientId)) return false;
    if (!matter.responsibleStaffId || !staffIds.has(matter.responsibleStaffId)) return false;
    if (!Array.isArray(matter.parties) || !Array.isArray(matter.tasks) || !Array.isArray(matter.activity)) return false;
    if (new Set(matter.parties.map((party) => party.id)).size !== matter.parties.length) return false;
    if (new Set(matter.tasks.map((task) => task.id)).size !== matter.tasks.length) return false;
    const client = value.clients.find((item) => item.id === matter.clientId);
    const hasClientParty = matter.parties.some((party) => party.relationship.toLowerCase().includes("client") && party.name === client?.displayName);
    return hasClientParty && matter.parties.every((party) => party.id && party.name && party.relationship)
      && matter.tasks.every((task) => task.id && task.title && task.status);
  });
}

export function loadPmsLabWorkspace() {
  const raw = deviceStorage.getItem(PMS_LAB_STORAGE_KEY);
  if (!raw) return createPmsLabSeed();
  try {
    const parsed = JSON.parse(raw);
    return validatePmsLabWorkspace(parsed) ? parsed : createPmsLabSeed();
  } catch {
    return createPmsLabSeed();
  }
}

export function savePmsLabWorkspace(workspace) {
  const value = { ...clone(workspace), updatedAt: new Date().toISOString() };
  if (!validatePmsLabWorkspace(value)) throw new Error("PMS Lab workspace is invalid.");
  deviceStorage.setItem(PMS_LAB_STORAGE_KEY, JSON.stringify(value));
  return value;
}

export function resetPmsLabWorkspace() {
  deviceStorage.removeItem(PMS_LAB_STORAGE_KEY);
  return createPmsLabSeed();
}

function required(value, label) {
  const normalized = String(value ?? "").trim();
  if (!normalized) throw new Error(`${label} is required.`);
  return normalized;
}

function nextNumber(records, field, prefix) {
  const highest = records.reduce((current, record) => {
    const match = String(record[field] ?? "").match(/(\d+)$/);
    return Math.max(current, match ? Number(match[1]) : 0);
  }, 0);
  return `${prefix}${String(highest + 1).padStart(4, "0")}`;
}

export function upsertPmsLabClient(workspace, input) {
  const givenName = required(input.givenName, "Given name");
  const familyName = required(input.familyName, "Family name");
  const existing = input.id ? workspace.clients.find((client) => client.id === input.id) : null;
  const id = existing?.id ?? `client_lab_${Date.now()}`;
  const client = {
    ...existing,
    id,
    clientNumber: existing?.clientNumber ?? nextNumber(workspace.clients, "clientNumber", "CL-"),
    displayName: `${givenName} ${familyName} (Sample)`,
    givenName,
    familyName,
    dateOfBirth: String(input.dateOfBirth ?? "Not recorded").trim() || "Not recorded",
    email: String(input.email ?? "").trim(),
    phone: String(input.phone ?? "").trim(),
    address: String(input.address ?? "").trim(),
  };
  const clients = existing
    ? workspace.clients.map((item) => item.id === id ? client : item)
    : [...workspace.clients, client];
  const matters = workspace.matters.map((matter) => matter.clientId !== id ? matter : {
    ...matter,
    parties: matter.parties.map((party) => party.relationship.toLowerCase().includes("client") ? { ...party, name: client.displayName } : party),
  });
  return savePmsLabWorkspace({ ...workspace, clients, matters });
}

export function upsertPmsLabMatter(workspace, input) {
  const clientId = required(input.clientId, "Client");
  const client = workspace.clients.find((item) => item.id === clientId);
  if (!client) throw new Error("The selected demonstration client does not exist.");
  const title = required(input.title, "Matter title");
  const existing = input.id ? workspace.matters.find((matter) => matter.id === input.id) : null;
  const id = existing?.id ?? `matter_lab_${Date.now()}`;
  const actor = workspace.staff.find((staff) => staff.id === input.responsibleStaffId)?.name ?? "PMS Lab user";
  const event = {
    id: `activity_lab_${Date.now()}`,
    date: new Date().toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }),
    actor,
    type: "Matter",
    detail: existing ? "Updated demonstration matter details." : "Created demonstration matter.",
  };
  const matter = {
    ...existing,
    id,
    matterNumber: existing?.matterNumber ?? nextNumber(workspace.matters, "matterNumber", "HL-2026-"),
    clientId,
    title,
    description: String(input.description ?? "").trim(),
    practiceArea: required(input.practiceArea, "Practice area"),
    status: String(input.status ?? "Open"),
    stage: String(input.stage ?? "Initial assessment").trim() || "Initial assessment",
    priority: String(input.priority ?? "Normal"),
    responsibleStaffId: required(input.responsibleStaffId, "Responsible staff member"),
    openedDate: existing?.openedDate ?? new Date().toLocaleDateString("en-AU", { dateStyle: "long" }),
    nextDate: String(input.nextDate ?? "Not listed").trim() || "Not listed",
    court: String(input.court ?? "Not allocated").trim() || "Not allocated",
    parties: existing?.parties
      ? existing.parties.map((party) => party.relationship.toLowerCase().includes("client") ? { ...party, name: client.displayName } : party)
      : [{ id: `party_lab_${Date.now()}`, name: client.displayName, relationship: "Client" }],
    tasks: existing?.tasks ?? [],
    activity: [event, ...(existing?.activity ?? [])],
  };
  const matters = existing
    ? workspace.matters.map((item) => item.id === id ? matter : item)
    : [matter, ...workspace.matters];
  return savePmsLabWorkspace({ ...workspace, matters });
}

function matterOrThrow(workspace, matterId) {
  const matter = workspace.matters.find((item) => item.id === matterId);
  if (!matter) throw new Error("The demonstration matter does not exist.");
  return matter;
}

function activity(workspace, type, detail, actorStaffId) {
  const actor = workspace.staff.find((staff) => staff.id === actorStaffId)?.name ?? "PMS Lab user";
  return {
    id: `activity_lab_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    date: new Date().toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }),
    actor,
    type,
    detail,
  };
}

function replaceMatter(workspace, nextMatter) {
  return savePmsLabWorkspace({
    ...workspace,
    matters: workspace.matters.map((matter) => matter.id === nextMatter.id ? nextMatter : matter),
  });
}

export function upsertPmsLabTask(workspace, matterId, input) {
  const matter = matterOrThrow(workspace, matterId);
  const title = required(input.title, "Task title");
  const existing = input.id ? matter.tasks.find((task) => task.id === input.id) : null;
  const assigneeStaffId = required(input.assigneeStaffId, "Assignee");
  const assignee = workspace.staff.find((staff) => staff.id === assigneeStaffId);
  if (!assignee) throw new Error("The selected demonstration assignee does not exist.");
  const task = {
    ...existing,
    id: existing?.id ?? `task_lab_${Date.now()}`,
    title,
    due: String(input.due ?? "Not set").trim() || "Not set",
    assigneeStaffId,
    assignee: assignee.name,
    status: String(input.status ?? "Not started"),
    priority: String(input.priority ?? "Normal"),
  };
  const tasks = existing
    ? matter.tasks.map((item) => item.id === task.id ? task : item)
    : [...matter.tasks, task];
  const event = activity(workspace, "Task", existing ? `Updated task: ${title}.` : `Created task: ${title}.`, assigneeStaffId);
  return replaceMatter(workspace, { ...matter, tasks, activity: [event, ...matter.activity] });
}

export function setPmsLabTaskComplete(workspace, matterId, taskId, complete) {
  const matter = matterOrThrow(workspace, matterId);
  const task = matter.tasks.find((item) => item.id === taskId);
  if (!task) throw new Error("The demonstration task does not exist.");
  const status = complete ? "Complete" : "In progress";
  const tasks = matter.tasks.map((item) => item.id === taskId ? { ...item, status } : item);
  const event = activity(workspace, "Task", `${complete ? "Completed" : "Reopened"} task: ${task.title}.`, task.assigneeStaffId);
  return replaceMatter(workspace, { ...matter, tasks, activity: [event, ...matter.activity] });
}

export function upsertPmsLabParty(workspace, matterId, input) {
  const matter = matterOrThrow(workspace, matterId);
  const name = required(input.name, "Party name");
  const relationship = required(input.relationship, "Relationship");
  const existing = input.id ? matter.parties.find((party) => party.id === input.id) : null;
  if (existing?.relationship.toLowerCase().includes("client")) {
    throw new Error("Edit the linked client record instead of the required client party.");
  }
  const party = { ...existing, id: existing?.id ?? `party_lab_${Date.now()}`, name, relationship };
  const parties = existing
    ? matter.parties.map((item) => item.id === party.id ? party : item)
    : [...matter.parties, party];
  const event = activity(workspace, "Party", existing ? `Updated party: ${name}.` : `Added party: ${name}.`, matter.responsibleStaffId);
  return replaceMatter(workspace, { ...matter, parties, activity: [event, ...matter.activity] });
}

export function removePmsLabParty(workspace, matterId, partyId) {
  const matter = matterOrThrow(workspace, matterId);
  const party = matter.parties.find((item) => item.id === partyId);
  if (!party) throw new Error("The demonstration party does not exist.");
  if (party.relationship.toLowerCase().includes("client")) {
    throw new Error("The linked client party cannot be removed from its matter.");
  }
  const parties = matter.parties.filter((item) => item.id !== partyId);
  const event = activity(workspace, "Party", `Removed party: ${party.name}.`, matter.responsibleStaffId);
  return replaceMatter(workspace, { ...matter, parties, activity: [event, ...matter.activity] });
}
